import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { getEvents } from '../helpers/events';
import { API_BASE_URL } from '../helpers/api';
import usePageTitle from '../hooks/usePageTitle';
import { renderHTML } from '../hooks/tools';
import '../css/Events.css';
import { toGuadeloupeDate, formatDisplayDate, formatDisplayTime, getCalendarDate } from '../hooks/dateUtils';

const Events = () => {
    usePageTitle('Événements');
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [eventDates, setEventDates] = useState(new Set());
    const [includePastEvents, setIncludePastEvents] = useState(false);

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        // Update event dates when events change
        const dates = new Set();
        events.forEach(event => {
            const dateString = getCalendarDate(event.startTime);
            dates.add(dateString);
        });
        setEventDates(dates);
    }, [events]);

    const fetchEvents = async () => {
        try {
            setIsLoading(true);
            const data = await getEvents();
            // console.log(data)
            const sortedEvents = data.sort((a, b) =>
                new Date(a.startTime) - new Date(b.startTime)
            );
            setEvents(sortedEvents);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: 'America/Guadeloupe'
        }).format(date);
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Guadeloupe'
        }).format(date);
    };

    // Calendar navigation
    const previousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const nextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    // Generate calendar days
    const generateCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        // Add empty days for padding
        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push({ day: '', empty: true });
        }

        // Add month days
        for (let day = 1; day <= lastDay.getDate(); day++) {
            const date = new Date(year, month, day);
            const dateString = getCalendarDate(new Date(year, month, day));
            const isToday = getCalendarDate(new Date()) === dateString;
            // const dateString = date.toISOString().split('T')[0];
            // const isToday = new Date().toISOString().split('T')[0] === dateString;

            days.push({
                day,
                date: dateString,
                isToday,
                hasEvents: eventDates.has(dateString),
                selected: dateString === selectedDate
            });
        }

        return days;
    };

    // Filter events based on search and selected date
    const filterEvents = () => {
        let filtered = [...events];
        const now = toGuadeloupeDate(new Date());  // Convert current time to Guadeloupe timezone
    
        // Filter past events unless toggle is on or date is selected
        if (!includePastEvents && !selectedDate) {
            filtered = filtered.filter(event => toGuadeloupeDate(event.startTime) > now);
        }
    
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(event =>
                event.name.toLowerCase().includes(search) ||
                event.description.toLowerCase().includes(search) ||
                (event.venueName && event.venueName.toLowerCase().includes(search))
            );
        }
    
        if (selectedDate) {
            filtered = filtered.filter(event => {
                const date = getCalendarDate(event.startTime);
                return date === selectedDate;
            });
        }
    
        return filtered;
    };

    // Group filtered events by date
    const groupEventsByDate = () => {
        const grouped = {};
        const filteredEvents = filterEvents();
      
        filteredEvents.forEach(event => {
          const dateString = getCalendarDate(event.startTime);
          if (!grouped[dateString]) {
            grouped[dateString] = [];
          }
          grouped[dateString].push(event);
        });
      
        return Object.fromEntries(
          Object.entries(grouped).sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
        );
      };

    const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

    return (
        <div className="events-page">
            <div className="events-hero">
                <div className="container">
                    <h1>Événements à venir</h1>
                    <div className="search-container">
                        <Search size={20} />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Rechercher un événement..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setSelectedDate('');
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="events-content">
                    {/* Calendar Sidebar */}
                    <div className="calendar-sidebar">
                        <h3>Calendrier</h3>
                        <div className="calendar">
                            <div className="calendar-header">
                                <button onClick={previousMonth}><ChevronLeft size={20} /></button>
                                <span>
                                    {new Intl.DateTimeFormat('fr-FR', {
                                        month: 'long',
                                        year: 'numeric'
                                    }).format(currentMonth)}
                                </span>
                                <button onClick={nextMonth}><ChevronRight size={20} /></button>
                            </div>

                            <div className="weekdays">
                                {weekDays.map(day => (
                                    <div key={day}>{day}</div>
                                ))}
                            </div>

                            <div className="days">
                                {generateCalendarDays().map((day, index) => (
                                    <div
                                        key={index}
                                        className={`day ${day.empty ? 'empty' : ''} 
                      ${day.isToday ? 'today' : ''} 
                      ${day.selected ? 'selected' : ''} 
                      ${day.hasEvents ? 'has-events' : ''}`}
                                        onClick={() => !day.empty && setSelectedDate(day.selected ? '' : day.date)}
                                    >
                                        {day.day}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="calendar-legend">
                            <div className="legend-item">
                                <div className="legend-dot"></div>
                                <span>Événements prévus</span>
                            </div>
                        </div>
                        <div className="past-events-toggle">
                            <label className="toggle-label">
                                <input
                                    type="checkbox"
                                    checked={includePastEvents}
                                    onChange={(e) => {
                                        setIncludePastEvents(e.target.checked);
                                        if (!e.target.checked) {
                                            setSelectedDate(''); // Clear selected date when hiding past events
                                        }
                                    }}
                                />
                                <span>Afficher les événements passés</span>
                            </label>
                        </div>
                    </div>

                    {/* Events Grid */}
                    <div className="results-container">
                        {isLoading ? (
                            <div className="loading">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : Object.entries(groupEventsByDate()).length === 0 ? (
                            <div className="no-events">
                                <h3>Aucun événement trouvé</h3>
                                <p>Modifiez vos critères de recherche ou revenez plus tard.</p>
                            </div>
                        ) : (
                            Object.entries(groupEventsByDate()).map(([date, dateEvents]) => (
                                <div key={date} className="events-section">
                                    <h2 className="date-header">{formatDisplayDate(dateEvents[0].startTime)}</h2>
                                    <div className="events-grid">
                                        {dateEvents.map(event => (
                                            <Link
                                                to={`/evenement/${event.documentId}`}
                                                key={event.id}
                                                className="event-card"
                                            >
                                                <div className="event-image">
                                                    <img
                                                        src={event.images[0]?.url
                                                            ? API_BASE_URL + event.images[0].url
                                                            : '/placeholder.jpg'}
                                                        alt={event.name}
                                                    />
                                                    <div className="event-date">
                                                        <Calendar size={16} />
                                                        {formatDisplayDate(event.startTime)}
                                                    </div>
                                                    <div className="event-time">
                                                        <Clock size={16} />
                                                        {formatDisplayTime(event.startTime)}
                                                    </div>
                                                </div>
                                                <div className="event-content">
                                                    <h3>{event.name}</h3>
                                                    {renderHTML(event.description, true, 120)}
                                                    <div className="event-meta">
                                                        {event.venueName && (
                                                            <span className="venue">
                                                                <MapPin size={16} />
                                                                {event.venueName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Events;